import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotificationPermission,
  requestNotificationPermission,
} from '../services/notificationService';
import WalkerBottomNav from '../components/WalkerBottomNav';
import './SettingsPage.css';

function Toggle({ on, onChange }) {
  return (
    <button
      className={`settings__toggle${on ? ' settings__toggle--on' : ''}`}
      onClick={() => onChange(!on)}
      role="switch"
      aria-checked={on}
    >
      <span className="settings__toggle-thumb" />
    </button>
  );
}

function Chevron() {
  return (
    <svg className="settings__row-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const areaName = sessionStorage.getItem('walkin_area_name') || '—';

  const [notifPerm, setNotifPerm] = useState(getNotificationPermission());
  const [notifJourney, setNotifJourney] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    setNotifPerm(getNotificationPermission());
  }, []);

  const notifDeals = notifPerm === 'granted';

  const handleNotifToggle = async (next) => {
    setShowIosHelp(false);
    if (!next) {
      // Can't programmatically revoke; tell user to manage in browser
      setShowIosHelp(true);
      return;
    }
    const result = await requestNotificationPermission();
    setNotifPerm(result);
    if (result === 'denied') setShowIosHelp(true);
  };

  const [showWipeModal, setShowWipeModal] = useState(false);

  function handleClearData() {
    setShowWipeModal(true);
  }

  function confirmWipe() {
    // Preserve shop account fields
    const preserved = {
      walkin_shop_mode:  sessionStorage.getItem('walkin_shop_mode'),
      walkin_shop_name:  sessionStorage.getItem('walkin_shop_name'),
      walkin_shop_email: sessionStorage.getItem('walkin_shop_email'),
      walkin_shop_area:  sessionStorage.getItem('walkin_shop_area'),
      walkin_shop_profile: sessionStorage.getItem('walkin_shop_profile'),
    };
    sessionStorage.clear();
    localStorage.clear();
    Object.entries(preserved).forEach(([k, v]) => {
      if (v !== null) sessionStorage.setItem(k, v);
    });
    navigate('/');
  }

  return (
    <div className="settings">
      <header
        className="settings__header"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 14px)' }}
      >
        <button className="settings__back" onClick={() => navigate(-1)} aria-label="Terug">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>
        <span className="settings__header-title">Instellingen</span>
        <div className="settings__header-gap" />
      </header>

      <main className="settings__main">

        {/* LOCATIE */}
        <div className="settings__section">
          <div className="settings__section-label">Locatie</div>
          <div className="settings__group">
            <button
              className="settings__row settings__row--tap"
              onClick={() => { window.location.href = 'app-settings:'; }}
            >
              <div className="settings__row-body">
                <div className="settings__row-title">Locatietoestemming</div>
                <div className="settings__row-sub">Beheer in Instellingen →</div>
              </div>
              <Chevron />
            </button>
            <div className="settings__divider" />
            <button className="settings__row settings__row--tap" onClick={() => navigate('/detect')}>
              <div className="settings__row-body">
                <div className="settings__row-title">Huidig gebied</div>
                <div className="settings__row-sub">{areaName}</div>
              </div>
              <Chevron />
            </button>
          </div>
        </div>

        {/* NOTIFICATIES */}
        <div className="settings__section">
          <div className="settings__section-label">Notificaties</div>
          <div className="settings__group">
            <div className="settings__row">
              <div className="settings__row-body">
                <div className="settings__row-title">Deal meldingen</div>
                <div className="settings__row-sub">
                  {notifPerm === 'granted'   && <span className="settings__row-status">Actief</span>}
                  {notifPerm === 'denied'    && <span className="settings__row-warn">Geweigerd — beheer in instellingen</span>}
                  {notifPerm === 'default'   && 'Nieuwe deals in jouw buurt'}
                  {notifPerm === 'unsupported' && 'Niet ondersteund op dit apparaat'}
                </div>
              </div>
              <Toggle on={notifDeals} onChange={handleNotifToggle} />
            </div>
            {showIosHelp && (
              <div className="settings__ios-help">
                Op iOS: Instellingen → Safari → Geavanceerd → Meldingen toestaan
                voor walkin.nl.
              </div>
            )}
            <div className="settings__divider" />
            <div className="settings__row">
              <div className="settings__row-body">
                <div className="settings__row-title">Journey herinneringen</div>
                <div className="settings__row-sub">Reminders voor geplande stops</div>
              </div>
              <Toggle on={notifJourney} onChange={setNotifJourney} />
            </div>
          </div>
        </div>

        {/* OVER WALKIN */}
        <div className="settings__section">
          <div className="settings__section-label">Over WalkIn</div>
          <div className="settings__group">
            <div className="settings__row">
              <div className="settings__row-body">
                <div className="settings__row-title">Versie</div>
              </div>
              <span className="settings__row-value">1.0.0</span>
            </div>
            <div className="settings__divider" />
            <button
              className="settings__row settings__row--tap"
              onClick={() => navigate('/privacy')}
            >
              <div className="settings__row-body">
                <div className="settings__row-title">Privacy verklaring</div>
              </div>
              <Chevron />
            </button>
            <div className="settings__divider" />
            <button
              className="settings__row settings__row--tap"
              onClick={() => navigate('/terms')}
            >
              <div className="settings__row-body">
                <div className="settings__row-title">Gebruiksvoorwaarden</div>
              </div>
              <Chevron />
            </button>
          </div>
        </div>

        <button className="settings__danger" onClick={handleClearData}>
          Alle data wissen
        </button>

        <p className="settings__footer">
          WalkIn · Versie 1.0.0<br />
          Deals om je heen. Direct zichtbaar.
        </p>

      </main>

      <WalkerBottomNav />

      {showWipeModal && (
        <div className="settings__modal" role="dialog">
          <div className="settings__modal-card">
            <h3 className="settings__modal-title">Alle data wissen?</h3>
            <p className="settings__modal-text">
              Verwijdert locatiegeschiedenis, voorkeuren en sessiedata. Je
              winkelier account blijft bewaard.
            </p>
            <button className="settings__modal-danger" onClick={confirmWipe}>
              Wis alles
            </button>
            <button
              className="settings__modal-cancel"
              onClick={() => setShowWipeModal(false)}
            >
              Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
